import type { FilterOption } from '@/components/filters/filter-menu'
import type { UnifiedCandidate } from '@/hooks/use-smart-search'

export type EvaluationResultSort = 'rank_desc' | 'rank_asc' | 'name_asc' | 'name_desc'

export const EVALUATION_RESULT_SORT_LABELS: Record<EvaluationResultSort, string> = {
  rank_desc: 'Highest Rank',
  rank_asc: 'Lowest Rank',
  name_asc: 'Name A–Z',
  name_desc: 'Name Z–A',
}

export const EVALUATION_RESULT_SORT_OPTIONS: FilterOption[] = (
  ['rank_desc', 'rank_asc', 'name_asc', 'name_desc'] as EvaluationResultSort[]
).map((key) => ({
  value: key,
  label: EVALUATION_RESULT_SORT_LABELS[key],
}))

function candidateRank(candidate: UnifiedCandidate): number {
  return candidate.rank ?? candidate.full?.rank ?? Number.MAX_SAFE_INTEGER
}

function candidateScore(candidate: UnifiedCandidate): number {
  return candidate.score ?? candidate.full?.final_score ?? 0
}

function candidateDisplayName(candidate: UnifiedCandidate): string {
  return candidate.name.trim() || 'Unknown'
}

export function compareEvaluationResults(
  a: UnifiedCandidate,
  b: UnifiedCandidate,
  sort: EvaluationResultSort,
): number {
  switch (sort) {
    case 'name_asc':
      return candidateDisplayName(a).localeCompare(candidateDisplayName(b), undefined, {
        sensitivity: 'base',
      })
    case 'name_desc':
      return candidateDisplayName(b).localeCompare(candidateDisplayName(a), undefined, {
        sensitivity: 'base',
      })
    case 'rank_asc': {
      const scoreDiff = candidateScore(a) - candidateScore(b)
      if (scoreDiff !== 0) return scoreDiff
      return candidateRank(b) - candidateRank(a)
    }
    case 'rank_desc':
    default: {
      const scoreDiff = candidateScore(b) - candidateScore(a)
      if (scoreDiff !== 0) return scoreDiff
      return candidateRank(a) - candidateRank(b)
    }
  }
}

export function sortEvaluationResults(
  candidates: UnifiedCandidate[],
  sort: EvaluationResultSort,
): UnifiedCandidate[] {
  return [...candidates].sort((a, b) => compareEvaluationResults(a, b, sort))
}

export function parseEvaluationResultSort(value: string | null): EvaluationResultSort {
  if (value === 'rank_asc' || value === 'name_asc' || value === 'name_desc') return value
  if (value === 'match_asc') return 'rank_asc'
  if (
    value === 'rank_desc' ||
    value === 'rank' ||
    value === 'match_desc'
  ) {
    return 'rank_desc'
  }
  return 'rank_desc'
}
